// MM-10 dashboard: monitora a saúde do pipeline multimodal nativo.
// Lê multimodal_metrics_hourly (RLS via security_invoker) + amostras
// recentes de multimodal_metrics. Reusa tokens semânticos do design
// system — sem cores hardcoded.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Image, Mic, Video, FileText, Link as LinkIcon, AlertTriangle } from "lucide-react";

interface HourlyRow {
  org_id: string;
  hour: string;
  kind: string;
  total: number;
  ok: number;
  errors: number;
  native_used: number;
  legacy_warned: number;
  avg_latency_ms: number | null;
  p50_latency_ms: number | null;
  p95_latency_ms: number | null;
  bytes_in: number | null;
  rejected_total: number | null;
}

interface RecentRow {
  id: string;
  created_at: string;
  kind: string;
  multimodal_used: boolean;
  fallback_level: number;
  legacy_warning_emitted: boolean;
  status: string;
  model_used: string | null;
  latency_ms: number | null;
  attachments_count: number;
  rejected_count: number;
  rejection_reasons: unknown;
}

function kindIcon(kind: string) {
  const cls = "h-4 w-4";
  switch (kind) {
    case "image":
    case "sticker":
    case "gif":
      return <Image className={cls} aria-hidden />;
    case "audio":
      return <Mic className={cls} aria-hidden />;
    case "video":
      return <Video className={cls} aria-hidden />;
    case "document":
      return <FileText className={cls} aria-hidden />;
    case "link":
      return <LinkIcon className={cls} aria-hidden />;
    default:
      return <AlertTriangle className={cls} aria-hidden />;
  }
}

function formatBytes(n: number | null): string {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

export default function CortexMultimodal() {
  const [hourly, setHourly] = useState<HourlyRow[]>([]);
  const [recent, setRecent] = useState<RecentRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const [{ data: hRows }, { data: rRows }] = await Promise.all([
        supabase
          .from("multimodal_metrics_hourly" as never)
          .select("*")
          .gte("hour", since)
          .order("hour", { ascending: false }),
        supabase
          .from("multimodal_metrics" as never)
          .select("*")
          .order("created_at", { ascending: false })
          .limit(50),
      ]);
      if (!cancelled) {
        setHourly((hRows as HourlyRow[]) ?? []);
        setRecent((rRows as RecentRow[]) ?? []);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = useMemo(() => {
    let total = 0, ok = 0, native = 0, legacy = 0, rejected = 0, bytes = 0;
    let latencySum = 0, latencyCount = 0;
    for (const h of hourly) {
      total += h.total;
      ok += h.ok;
      native += h.native_used;
      legacy += h.legacy_warned;
      rejected += h.rejected_total ?? 0;
      bytes += h.bytes_in ?? 0;
      if (h.avg_latency_ms != null) {
        latencySum += h.avg_latency_ms * h.total;
        latencyCount += h.total;
      }
    }
    const successRate = total ? Math.round((ok / total) * 100) : 0;
    const nativeRate = total ? Math.round((native / total) * 100) : 0;
    const avgLatency = latencyCount ? Math.round(latencySum / latencyCount) : 0;
    return { total, successRate, nativeRate, legacy, rejected, bytes, avgLatency };
  }, [hourly]);

  const byKind = useMemo(() => {
    const map = new Map<string, { total: number; ok: number; native: number }>();
    for (const h of hourly) {
      const cur = map.get(h.kind) ?? { total: 0, ok: 0, native: 0 };
      cur.total += h.total;
      cur.ok += h.ok;
      cur.native += h.native_used;
      map.set(h.kind, cur);
    }
    return [...map.entries()].sort((a, b) => b[1].total - a[1].total);
  }, [hourly]);

  return (
    <div className="container mx-auto space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">Cortex · Multimodal</h1>
        <p className="text-sm text-muted-foreground">
          Saúde do pipeline multimodal nativo (últimas 24h). Sem base64 — métricas agregadas apenas.
        </p>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando métricas…
        </div>
      ) : (
        <>
          <section
            aria-label="Resumo 24h"
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
          >
            <SummaryCard label="Mensagens com mídia" value={totals.total.toLocaleString("pt-BR")} />
            <SummaryCard
              label="Sucesso"
              value={`${totals.successRate}%`}
              tone={totals.successRate >= 95 ? "good" : totals.successRate >= 80 ? "warn" : "bad"}
            />
            <SummaryCard
              label="Pipeline nativo usado"
              value={`${totals.nativeRate}%`}
              tone={totals.nativeRate >= 80 ? "good" : "warn"}
              hint={`${totals.legacy} fallbacks com aviso legado`}
            />
            <SummaryCard
              label="Latência média"
              value={`${totals.avgLatency} ms`}
              hint={`${formatBytes(totals.bytes)} processados · ${totals.rejected} rejeições`}
            />
          </section>

          <Card>
            <CardHeader>
              <CardTitle>Por tipo de mídia</CardTitle>
            </CardHeader>
            <CardContent>
              {byKind.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem amostras nas últimas 24h.</p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {byKind.map(([kind, agg]) => {
                    const successPct = agg.total ? Math.round((agg.ok / agg.total) * 100) : 0;
                    const nativePct = agg.total ? Math.round((agg.native / agg.total) * 100) : 0;
                    return (
                      <div
                        key={kind}
                        className="flex items-center justify-between rounded-lg border bg-card p-4"
                      >
                        <div className="flex items-center gap-3">
                          {kindIcon(kind)}
                          <div>
                            <div className="font-medium capitalize">{kind}</div>
                            <div className="text-xs text-muted-foreground">
                              {agg.total} eventos · {successPct}% ok · {nativePct}% nativo
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Eventos recentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quando</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Modelo</TableHead>
                      <TableHead>Pipeline</TableHead>
                      <TableHead>Latência</TableHead>
                      <TableHead>Anexos</TableHead>
                      <TableHead>Rejeições</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {new Date(r.created_at).toLocaleString("pt-BR")}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 capitalize">
                            {kindIcon(r.kind)} {r.kind}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {r.model_used ?? "—"}
                        </TableCell>
                        <TableCell>
                          {r.multimodal_used ? (
                            <Badge variant="default">nativo · L{r.fallback_level}</Badge>
                          ) : r.legacy_warning_emitted ? (
                            <Badge variant="destructive">legado · aviso</Badge>
                          ) : (
                            <Badge variant="secondary">legado</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{r.latency_ms ?? "—"} ms</TableCell>
                        <TableCell className="text-xs">{r.attachments_count}</TableCell>
                        <TableCell className="text-xs">
                          {r.rejected_count > 0 ? (
                            <span className="text-destructive">
                              {r.rejected_count}
                              {Array.isArray(r.rejection_reasons) && r.rejection_reasons.length > 0 ? (
                                <span className="ml-1 text-muted-foreground">
                                  ({(r.rejection_reasons as string[]).slice(0, 2).join(", ")})
                                </span>
                              ) : null}
                            </span>
                          ) : (
                            "0"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {recent.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                          Nenhum evento multimodal recente.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "good" | "warn" | "bad" | "neutral";
}) {
  const toneClass =
    tone === "good"
      ? "text-primary"
      : tone === "warn"
      ? "text-amber-600 dark:text-amber-500"
      : tone === "bad"
      ? "text-destructive"
      : "text-foreground";
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}
